<template>
<div class="chapter">
<div class="inner_page">
<h2 class="ph">III.</h2>
<p class="calibre3">Each of the Second-Stage Lensmen had exactly the same facts, the
same data, upon which to theorize and from which to draw conclusions.
Each had shared his experiences, his findings, and his deductions and
inductions with all of the others. They had discussed minutely, in
wide-open four-ways, every phase of the Boskonian problem. Nevertheless
the approach of each to that problem and the point of attack chosen by
each was individual and characteristic.</p>
<p class="calibre3">Kimball Kinnison was by nature forthright; direct. As has been seen, he
could use the approach circuitous if necessary, but he much preferred
and upon every possible occasion employed the approach direct. He
liked plain, unambiguous clues much better than obscure ones; the more
obvious and factual the clue was, the better he liked it.</p>
<p class="calibre3">He was now, therefore, heading for Antigan IV, the scene of the latest
and apparently the most outrageous of a long series of crimes of
violence. He didn't know much about it; the request had come in through
regular channels, not via Lens, that he visit Antigan and take personal
charge of the investigation of the supposed murder of the Planetary
President.</p>
<p class="calibre3">As his speedster flashed through space the Gray Lensman mulled over
in his mind the broad aspects of this crime wave. It was spreading
far and wide, and the wider it spread and the intenser it became the
more vividly one salient fact stuck out. Selectivity—distribution.
The solar systems of Thrale, Velantia, Tellus, Klovia, and Palain had
not been affected. Thrale, Tellus, and Klovia were full of Lensmen.
Velantia, Rigel, Palain, and a good part of the time Klovia, were
the working headquarters of Second-Stage Lensmen. It seemed, then,
that the trouble was roughly in inverse ratio to the numbers or the
abilities of the Lensmen in the neighborhood. Something, therefore,
that Lensmen—particularly Second-Stage Lensmen—were bad for. That
was true, of course, for all crime. Nevertheless, this seemed to be a
special case.</p>
<div class="figcenter2" id="illus3">
<img alt="" class="w" id="id-5883219459375073399" src="Images/img0018.jpg"/>
</div>
<p class="calibre3">And when he reached his destination he found out that it was. The
planet was seething. Its business and its everyday activities seemed to
be almost paralyzed. Martial law had been declared; the streets were
practically deserted except for thick-clustered groups of heavily-armed
guards. What few people were abroad were furtive and sly; slinking
hastily along with their fear-filled eyes trying to look in all
directions at once.</p>
<p class="calibre3">"QX, Wainwright, go ahead," Kinnison directed brusquely when,
alone with the escorting Patrol officers in a shielded car, he
was being taken to the Capitol grounds. "There's been too much
secrecy—pussyfooting—about the whole affair. Spill it, please."</p>
<p class="calibre3">"Very well, sir," and Wainwright told his tale. Things had been
happening for months. Little things, but disturbing. Then murders and
kidnapings and unexplained disappearances had begun to increase. The
police forces had been falling farther and farther behind. The usual
cries of incompetence and corruption had been raised, only further
to confuse the issue. Circulars—dodgers—hand-bills appeared all
over the planet; from where nobody knew. The keenest detectives could
find no clue to papermakers, printers, or distributors. The usual
inflammatory, subversive propaganda—"Down with the Patrol!" "Give us
back our freedom!" and so on—but, because of the high tension already
prevailing, the stuff had been unusually effective in breaking down the
morale of the citizenry as a whole.</p>
<p class="calibre3">"Then this last thing. For two solid weeks the whole world was
literally plastered with the announcement that at midnight on
the thirty-fourth of Dreel—you're familiar with our calendar, I
think?—President Renwood would disappear. Two weeks warning—daring
us." Wainwright got that far and stopped.</p>
<p class="calibre3">"Well, go on. He disappeared, I know. How? What did you fellows do to
prevent it? Why all the secrecy?"</p>
<p class="calibre3">"If you insist, I'll have to tell you, of course, but I'd rather not."
Wainwright flushed uncomfortably. "You wouldn't believe it. Nobody
could. I wouldn't believe it myself if I hadn't been there. I'd rather
you'd wait, sir, and let the Vice President tell you, in the presence
of the Treasurer and the others who were on duty that night."</p>
<p class="calibre3">"Um-m-m ... I see ... maybe." Kinnison's mind raced. "That's why
nobody would give me details? Afraid I wouldn't believe it ... that I'd
think they'd been—" He stopped. "Hypnotized" would have been the next
word, but that would have been jumping at conclusions. Even if true,
there was no sense in airing that hypothesis—yet.</p>
<p class="calibre3">"Not afraid, sir. They <i class="calibre9">knew</i> that you wouldn't believe it."</p>
<p class="calibre3">After entering Government Reservation they went, not to the president's
private quarters, but into the Treasury and down into the subbasement
housing the most massive, the most utterly impregnable vault of the
planet. There the nation's most responsible officers told Kinnison,
with their entire minds as well as their tongues, what had happened.</p>
<p class="calibre3">Upon that black day business had been suspended. No visitors of any
sort had been permitted to enter the Reservation. No one had been
allowed to approach the president except old and trusted officers about
whose loyalty there could be no question. Airships and spaceships
had filled the sky. Troops, armed with semiportables or manning
fixed-mount heavy stuff, had covered the grounds. At five minutes
before midnight Renwood, accompanied by four secret service men, had
entered the vault, which was thereupon locked by the treasurer. All
the cabinet members saw them go in, as did the attendant corps of
specially-selected guards. Nevertheless, when the treasurer opened the
vault at five minutes after midnight, the five men were gone. No trace
of any one of them had been found from that time on.</p>
<p class="calibre3">"And that—every word of it—is TRUE!" the assembled minds yelled as
one, all unconsciously, into the mind of the Lensman.</p>
<p class="calibre3">During all this telling Kinnison had been searching mind after mind;
inspecting each minutely for the telltale marks of mental surgery. He
found none. No hypnosis. This thing had happened, exactly as they told
it. Now, convinced of that fact, his eyes clouded with foreboding,
he sent out his sense of perception and studied the vault itself.
Millimeter by cubic millimeter he scanned the innermost details of
its massive structure—the concrete, the neo-carballoy, the steel,
the heat-conductors and the closely-spaced gas cells. He traced the
intricate wiring of the networks of alarms. Everything was sound.
Everything functioned. Nothing had been disturbed.</p>
<p class="calibre3">The sun of this system, although rather on the small side, was
intensely hot; this planet, Four, was a long way out. Pretty
close to Cardynge's limit ... or the Boskonians had improved their
technique—tightened up their controls. A tube, of course ... for all
the tea in China it had to be a tube. Kinnison sagged; for the first
time in his life the indomitable Gray Lensman showed his years and more.</p>
<p class="calibre3">"I know that it happened." His voice was grim, quiet, as he spoke to
the still protesting men. "I also know how it was done, but that's all."</p>
<p class="calibre3">"HOW?" they demanded, practically in one voice.</p>
<p class="calibre3">"A hyperspatial tube," and Kinnison went on to explain, as well as
he could, the functioning of a thing which could not be grasped
intrinsically by any nonmathematical three-dimensional mind.</p>
<p class="calibre3">"But what can we or you or anybody else <i class="calibre9">do</i> about it?" the
treasurer asked, numbly.</p>
<p class="calibre3">"Nothing whatever." Kinnison's voice was flat. "When it's gone, it's
gone. Where does the light go when a lamp goes out? No more trace.
No more way—no way whatever—of tracing it. Hundreds of millions of
planets in this galaxy, as many in the Second. Millions and millions of
galaxies. All that in one Universe—our own universe. And there are an
infinite number—too many to be expressed, let alone to be grasped—of
universes, side by side, like pages in a book except thinner, in the
hyperdimension. So you can figure out for yourselves the chances of
ever finding either President Renwood or the Boskonians who took
him—so close to zero as to be indistinguishable from zero absolute."</p>
<p class="calibre3">The treasurer was crushed. "Do you mean to say that there is no
protection at all from this thing? That they can keep on doing away
with us just as they please? The nation is going mad, sir, day by
day—one more such occurrence and we will be a planet of maniacs."</p>
<p class="calibre3">"Oh, no—I didn't say that." The tension lightened. "Just that we can't
do anything about the president and his aides. The tube can be detected
while it is in place, and anyone coming through it can be shot as soon
as he can be seen. What you need is a couple of Rigellian Lensmen, or
Ordoviks. I'll see to it that you get them. I don't think, with them
here, that they will even try to repeat." He did not add what he knew
somberly to be a fact, that the enemy would go elsewhere, to some other
planet not protected by a Lensman able to perceive the intangible
structure of a sphere of pure force.</p>
<p class="calibre3">Frustrated, the Lensman again took to space. It was terrible,
this thing of having everything happening where he wasn't,
and when he got there having nothing left to work on.
Hit-and-run—stab-in-the-back—how could a man fight something that he
couldn't see or sense or feel or find? But this chewing his fingernails
to the elbow wasn't getting him anywhere, either; he'd have to find
something that he <i class="calibre9">could</i> stick a tooth into. What?</p>
<p class="calibre3">All former avenues of approach were blocked; he was sure of that.
The Boskonians, who were now in charge of things, could really think.
No underling would know anything about any one of them except at such
times and places as the directors chose, and those conferences would be
as nearly detection-proof as they could be made. What to do?</p>
<p class="calibre3">Easy. Catch a big operator in the act. He grinned wryly to himself.
Easy to say, but not—However, it wasn't impossible. The Boskonians
were not supermen—they didn't have any more jets than he did. Put
himself in the other fellow's place—what would he do if he were a
Boskonian big shot? He had had quite a lot of experience in the role.
Were there any specific groups of crimes which revealed techniques
similar to those which he himself would use in like case?</p>
<p class="calibre3">He, personally, preferred to work direct and to attack in force. At
need, however, he had done a smooth job of boring from within. In the
face of the Patrol's overwhelming superiority of armament, especially
in the First Galaxy, they would have to bore from within. How? By
what means? He was a Lensman; they were not. Jet back! Or were they,
perhaps? How did he know that they weren't? Maybe they were, by this
time. Fossten the renegade Arisian—No use kidding himself; Fossten
might have known as much about the Lens as Mentor himself, and might
have developed an organization that even Mentor didn't know anything
about. Or Mentor might be figuring that it would be good for what
ailed a certain fat-headed Gray Lensman to have to dope this out for
himself. QX.</p>
<p class="calibre3">He shot a call to Vice Co-ordinator Maitland, who was now in complete
charge of the office which Kinnison had temporarily abandoned.</p>
<p class="calibre3">"Cliff? Kim. Just gave birth to an idea." He explained rapidly what the
idea was. "Maybe nothing to it, but we'd better get up on our toes and
find out. You might suggest to the boys that they check up here and
there, particularly around the rough spots. If any of them find any
trace anywhere of off-color, sour, or even slightly rancid Lensmanship,
with or without a Lens appearing in the picture, burn a hole in space
getting it to me. QX?... Thanks."</p>
<p class="calibre3">Viewed in this new perspective, Renwood of Antigan IV might have been
neither a patriot nor a victim, but a saboteur. The tube could have
been a prop, used deliberately to cap the mysterious climax. The four
honest and devoted guards were the real casualties. Renwood—or whoever
he was—having accomplished his object of undermining and destroying
the whole planet's morale, might simply have gone elsewhere to continue
his nefarious activities. It was fiendishly clever. That spectacularly
theatrical finale was certainly one for the book. The whole thing,
though, was very much of a piece in quality of workmanship with what
he had done in becoming the Tyrant of Thrale. Farfetched? No. He had
already denied in his thoughts that the Boskonian operators were
supermen. Conversely, he wasn't, either. He would have to admit that
they might very well be as good as he was; to deny them the ability to
do anything which he himself could do would be sheer stupidity.</p>
<p class="calibre3">Where did that put him? On Radelix, by Klono's golden gills! A
good-sized planet. Important enough, but not too much so. People human.
Comparatively little hell being raised there—yet. Very few Lensmen,
and Gerrond the top. Hm-m-m. Gerrond. Not too bright, as Lensmen went,
and inclined to be a bit brass-hattish. To Radelix, by all means, next.</p>
<p class="calibre3">He went to Radelix, but not in the <i class="calibre9">Dauntless</i> and not in gray.
He was a passenger upon a luxury liner, a writer in search of local
color for another saga of the spaceways. Sybly Whyte—one of the
Patrol's most carefully-established figments—had a bulletproof past.
His omnivorous interest and his uninhibited nosiness were the natural
attributes of his profession—everything is grist which comes to an
author's mill.</p>
<p class="calibre3">Sybly Whyte then prowled about Radelix. Industriously and, to some
observers, pointlessly. He and his red-leather notebook were apt to
be seen anywhere at any time, day or night. He visited spaceports,
he climbed through freighters, he lost small sums in playing various
games of so-called chance in spacemen's dives. Upon the other hand, he
truckled assiduously to the social elite and attended all functions
into which he could wangle or could force his way. He made a pest of
himself in the offices of politicians, bankers, merchant princes,
tycoons of business and manufacture, and all other sorts of greats.</p>
<p class="calibre3">He was stopped one day in the outer office of an industrial potentate.
"Get out and stay out," a peg-legged guard told him. "The boss hasn't
read any of your stuff, but I have, and neither of us wants to talk to
you. Data, huh? What do you need of data on atomic cats and bulldozers
to write them space operas of yours? Why don't you get a roustabout job
on a freighter and learn something about what you're trying to write
about? Get yourself a real space tan instead of that imitation you
got under a lamp; work some of that lard off of your carcass!" Whyte
was definitely fatter than Kinnison had been; and, somehow, softer;
he peered owlishly through heavy lenses which, fortunately, did not
interfere with his sense of perception. "Then maybe some of your tripe
will be half-fit to read—beat it!"</p>
<p class="calibre3">"Yes, sir. Thank you, sir; very much, sir." Kinnison bobbed
obsequiously and scurried out, writing industriously in his notebook
the while. He had, however, found out what he wanted to know. The boss
was nobody he was looking for.</p>
<p class="calibre3">Nor was an eminent statesman whom he buttonholed at a reception. "I
fail to see, sir, entirely, any point in your interviewing <i class="calibre9">me</i>,"
that worthy informed him, frigidly. "I am not, I am ... uh ... sure,
suitable material for any opus upon which you may be at work."</p>
<p class="calibre3">"Oh, you can't ever tell, sir," Kinnison said. "You see, I never know
who or what is going to get into any of my stories until after I start
to write it, and sometimes not even then." The statesman glared and
Kinnison retreated in disorder.</p>
<p class="calibre3">To stay in character Kinnison actually wrote a story while upon
Radelix; a story which was later acclaimed as one of Sybly Whyte's best.</p>
<p class="calibre3">"Qadgop the Mercotan slithered flatly around the after-bulge of the
tranship. One claw dug into the meters-thick armor of pure neutronium,
then another. Its terrible xmexlike snout locked on. Its zymolosely
polydactile tongue crunched out, crashed down, rasped across. <i class="calibre9">Slurp!
Slurp!</i> At each abrasive stroke the groove in the tranship's plating
deepened and Qadgop leered more fiercely. Fools! Did they think that
the airlessness of absolute space, the heatlessness of absolute zero,
the yieldlessness of absolute neutronium, could stop QADGOP THE
MERCOTAN? And the stowaway, that human wench Cynthia, cowering in
helpless terror just beyond this thin and fragile wall—" Kinnison was
tapping merrily and verbosely along, at a cento a word, when his first
real clue developed.</p>
<p class="calibre3">A yellow "attention" light gleamed upon his visiphone panel, a
subdued chime gave notice that a message of importance was about to be
broadcast to the world. Kinnison-Whyte flipped his switch and the stern
face of the Provost Marshal appeared upon the screen.</p>
<p class="calibre3">"Attention, please," the image spoke. "Every citizen of Radelix is
urged to be upon the lookout for the source of certain inflammatory and
subversive literature which is beginning to appear in various cities of
this planet. Our officers cannot be everywhere at once; you citizens
are. It is hoped that by the aid of your vigilance this threat to our
planetary peace and security can be removed before it becomes really
serious; that we can avoid the imposition of martial law."</p>
<p class="calibre3">This message, while not of extreme or urgent import to most
Radeligians, held for Kinnison a profound and unique meaning. He was
right. He had deduced the thing one hundred percent. He knew what was
going to happen next, and how; he knew that neither the law-enforcement
officers of Radelix nor its massed citizenry could stop it. They could
not even impede it. A force of Lensmen could stop it—but that would
not get the Patrol anywhere unless they could capture or kill the
beings really responsible for what was done. To alarm them would not do.</p>
<p class="calibre3">Whether or not he could do much of anything before the grand climax
depended upon a lot of factors. Upon what that climax was; upon who
was threatened with what; upon whether or not the threatened one was
actually a Boskonian. A great deal of investigation was indicated.</p>
<p class="calibre3">If the enemy were going to repeat, as seemed probable, the president
would be the victim. If he, Kinnison, could not get a line upon the
higher-ups before the plot came to a head, he would have to let it
develop right up to the point of disappearance; and for Whyte to appear
upon the scene at that time would be to attract undesirable attention.
No—by that time he must already have been kicking around underfoot
long enough to have become an unnoticeable fixture.</p>
<p class="calibre3">Wherefore he moved into quarters as close to the Executive Offices
as he could possibly get; and in those quarters he worked openly
and wordily at the bringing of the affair of Qadgop and the
beautiful-but-dumb Cynthia to a satisfactory conclusion.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0004.vue?4d1b0cf6